/**
 * Admin entities page.
 *
 * CP1 (Slice 2) shipped this as a read-only dense table sourced from the
 * public `entities.json` envelope. CP17 Phase 2 retargets the data
 * source to the admin list endpoint (`/v2/staging/entities/admin/list.json`)
 * because the public envelope intentionally omits the row id, and the
 * Edit affordance needs id-keyed URLs.
 *
 * The user-visible behavior preserved from CP1: dense table of entities,
 * sorted alphabetically, one row per entity.
 *
 * The user-visible additions for CP17 Phase 2:
 *   - one new "Edit" action column (rightmost) linking to
 *     `/entities/:id/edit`
 *   - soft-merged rows render the Edit affordance disabled (italic
 *     "merged" label) so the operator can see them but not click into
 *     a form they couldn't submit
 *   - a truncation banner if the loader hit its hard cap (currently
 *     500 rows)
 *
 * Out of scope for this slice: aliases, social links, active-arc
 * participation, sentiment columns. They remain visible on the public
 * Players page; the admin SPA's edit flow doesn't need them. The
 * original DenseTable + EntityRow components are retained unmodified
 * for the public-data-shaped surface.
 */

import type { EntitiesAdminListItem } from '@fishstank/contracts'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { FetchedArtifact } from '../components/FetchedArtifact'
import { Shell } from '../layouts/Shell'
import { fetchEntitiesForAdmin } from '../lib/worker'

export function EntitiesPage() {
  const query = useQuery({
    queryKey: ['entities', 'admin', 'list'],
    queryFn: fetchEntitiesForAdmin,
  })

  const result = query.data
  const provenance =
    result && (result.kind === 'ok' || result.kind === 'empty') ? result.artifact.provenance : null

  return (
    <Shell provenance={provenance}>
      <header className="mb-6">
        <h1 className="text-2xl font-bold">Entities</h1>
        <p className="mt-1 text-sm text-slate-400">
          All entities. Click Edit to modify the four core fields (display name, slug, kind,
          status). Soft-merged rows are read-only in this slice.
        </p>
      </header>

      <FetchedArtifact
        result={result}
        isLoading={query.isLoading}
        testidPrefix="entities"
        labels={{
          loading: 'Loading entities…',
          errorTitle: "Couldn't load entities.",
          empty: 'No entities yet.',
        }}
      >
        {(artifact) => (
          <>
            {artifact.body.truncated && (
              <div
                data-testid="entities-truncated-banner"
                className="mb-4 rounded border border-amber-700 bg-amber-950/40 p-3 text-sm text-amber-100"
              >
                Showing the first {artifact.body.entities.length} entities; more are present. A
                search affordance will land in a future checkpoint.
              </div>
            )}
            <AdminEntitiesTable entities={artifact.body.entities} />
          </>
        )}
      </FetchedArtifact>
    </Shell>
  )
}

interface AdminEntitiesTableProps {
  entities: readonly EntitiesAdminListItem[]
}

function AdminEntitiesTable({ entities }: AdminEntitiesTableProps) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-800">
      <table
        data-testid="entities-admin-table"
        data-entity-count={entities.length}
        className="min-w-full divide-y divide-slate-800 text-sm"
      >
        <thead className="bg-fishstank-surface text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Display name</th>
            <th className="px-3 py-2 text-left font-medium">Slug</th>
            <th className="px-3 py-2 text-left font-medium">Kind</th>
            <th className="px-3 py-2 text-left font-medium">Status</th>
            <th className="px-3 py-2 text-left font-medium">Updated</th>
            <th className="px-3 py-2 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-900 bg-fishstank-ink/40">
          {entities.map((entity) => (
            <AdminEntityRow key={entity.id} entity={entity} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface AdminEntityRowProps {
  entity: EntitiesAdminListItem
}

function AdminEntityRow({ entity }: AdminEntityRowProps) {
  const isMerged = entity.status === 'merged' || entity.merged_into_entity_id !== null
  return (
    <tr data-testid={`entities-admin-row-${entity.id}`} data-entity-slug={entity.slug}>
      <td className="px-3 py-2 text-slate-100">{entity.primary_name}</td>
      <td className="px-3 py-2 font-mono text-xs text-slate-300">{entity.slug}</td>
      <td className="px-3 py-2 text-slate-300">{entity.kind}</td>
      <td className="px-3 py-2 text-slate-300">{entity.status}</td>
      <td className="px-3 py-2 text-xs text-slate-500">{entity.updated_at.slice(0, 10)}</td>
      <td className="px-3 py-2 text-right">
        {isMerged ? (
          <span
            data-testid={`entities-admin-merged-${entity.id}`}
            className="text-xs italic text-slate-500"
            title="Soft-merged rows are read-only in this slice."
          >
            merged
          </span>
        ) : (
          <Link
            to={`/entities/${entity.id}/edit`}
            data-testid={`entities-admin-edit-link-${entity.id}`}
            className="text-fishstank-accent underline-offset-2 hover:underline"
          >
            Edit
          </Link>
        )}
      </td>
    </tr>
  )
}
